<script setup lang="ts">
import InvoiceUploader from '@/components/admin/common/InvoiceUploader.vue';
import RenderInvoice from '@/components/admin/common/RenderInvoice.vue';
import AdminCreateInvoiceNewSupplier, {
  type NewSupplierSaveEmit,
} from '@/components/admin/create-invoice/AdminCreateInvoiceNewSupplier.vue';
import DropDown, { type DropDownItem } from '@/components/common/DropDown.vue';
import ProjectsDropDown from '@/components/common/projects/ProjectsDropDown.vue';
import { financialConverter } from '@/converters/financials/financials-converter';
import { RouteName } from '@/router/models/route-name';
import { adminSupplierInvoiceService } from '@/services/admin/admin-supplier-invoice-service.ts';
import { adminSupplierService } from '@/services/admin/admin-supplier-service';
import { overlaySpinnerService } from '@/services/app/overlay-spinner-service';
import { notificationService } from '@/services/notification/notification-service';
import { outlookInboxService } from '@/services/outlook-inbox/outlook-inbox-service';
import { fetchAccounts } from '@/utils/accounts/fetch-accounts.ts';
import { FinancialConverterError } from '@/utils/errors/converters/financial-converter-errors';
import { PhPlus } from '@phosphor-icons/vue';
import { isClientError, models } from 'cesg-shared';
import { VatType } from 'cesg-shared/dist/models';
import { computed, onActivated, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

const uploadedFile = ref<models.SupplierInvoiceFile | null>(null);
const isNewSupplierModalOpen = ref(false);

const handleFileLoaded = (file: models.SupplierInvoiceFile) => {
  uploadedFile.value = file;
  isFromInboxDocument.value = false;
};

const { t } = useI18n();
const route = useRoute();

interface FormField {
  id: string;
  label: string;
  value: string;
  paymentType?: 'BG' | 'PG';
}

interface FormDataStructure {
  selectedSupplier: string;
  leftColumn: FormField[];
  rightColumn: FormField[];
  bottomRows: FormField[];
  note: string;
}

type ApiValue = string | number | null | undefined;

const suppliers = ref<models.AdminGetSuppliersResponse>([]);
const accounts = ref<DropDownItem[]>([]);
const currentSupplierNumber = ref<string>('');
const isFromInboxDocument = ref(false);
const errorFields = ref<Set<string>>(new Set());

const router = useRouter();

const formData = reactive<FormDataStructure>({
  selectedSupplier: '',
  leftColumn: [
    {
      id: 'f1',
      label: t('adminCreateInvoice.leftColumn.organisationNumber'),
      value: '',
    },
    {
      id: 'f2',
      label: t('adminCreateInvoice.leftColumn.bankGiro-plusGiro'),
      value: '',
      paymentType: 'BG',
    },
    {
      id: 'f3',
      label: t('adminCreateInvoice.leftColumn.streetName'),
      value: '',
    },
    {
      id: 'f35',
      label: t('adminCreateInvoice.leftColumn.buildingNumber'),
      value: '',
    },
    {
      id: 'f4',
      label: t('adminCreateInvoice.leftColumn.postNumber'),
      value: '',
    },
    { id: 'f5', label: t('adminCreateInvoice.leftColumn.city'), value: '' },
    {
      id: 'f8',
      label: t('adminCreateInvoice.leftColumn.vatNumber'),
      value: '',
    },
  ],
  rightColumn: [
    {
      id: 'f9',
      label: t('adminCreateInvoice.rightColumn.invoiceDate'),
      value: '',
    },
    {
      id: 'f10',
      label: t('adminCreateInvoice.rightColumn.dueDate'),
      value: '',
    },
    {
      id: 'f11',
      label: t('adminCreateInvoice.rightColumn.termsOfPayment'),
      value: '',
    },
    { id: 'f12', label: t('adminCreateInvoice.rightColumn.ocr'), value: '' },
    {
      id: 'f125',
      label: t('adminCreateInvoice.rightColumn.pennyAdjust'),
      value: '',
    },
    {
      id: 'f13',
      label: t('adminCreateInvoice.rightColumn.totalAmount'),
      value: '',
    },
    {
      id: 'f14',
      label: t('adminCreateInvoice.rightColumn.vat'),
      value: '',
    },
    {
      id: 'f15',
      label: t('adminCreateInvoice.rightColumn.invoiceNumber'),
      value: '',
    },
    {
      id: 'f16',
      label: t('adminCreateInvoice.rightColumn.currency'),
      value: 'SEK',
    },
  ],
  bottomRows: [
    {
      id: 'bottom1',
      label: t('adminCreateInvoice.bottomRows.account'),
      value: '',
    },
    {
      id: 'bottom2',
      label: t('adminCreateInvoice.bottomRows.project'),
      value: '',
    },
    {
      id: 'bottom3',
      label: t('adminCreateInvoice.bottomRows.vatType'),
      value: '',
    },
  ],
  note: '',
});

const formatSupplierName = ({
  supplierNumber,
  name,
}: {
  supplierNumber?: string;
  name: string;
}) => {
  return supplierNumber ? `${supplierNumber} - ${name}` : name;
};

const supplierOptions = computed<DropDownItem[]>(() => {
  return suppliers.value.map((supplier) => ({
    value: supplier.name,
    text: formatSupplierName({
      ...supplier,
    }),
  }));
});

const vatTypeOptions = computed<DropDownItem[]>(() =>
  Object.values(VatType).map((value) => ({
    value,
    text: t(`adminCreateInvoice.bottomRows.vatTypes.${value}`),
  })),
);

const totalField = computed(() =>
  formData.rightColumn.find((f) => f.id === 'f13'),
);
const vatField = computed(() =>
  formData.rightColumn.find((f) => f.id === 'f14'),
);
const selectedVatType = computed(
  () => formData.bottomRows.find((f) => f.id === 'bottom3')?.value,
);

const currencyOptions = computed<DropDownItem[]>(() => [
  {
    value: 'SEK',
    text: 'SEK',
  },
]);

const calculateDaysDiff = () => {
  const invoiceDateStr = formData.rightColumn.find((f) => f.id === 'f9')?.value;
  const dueDateStr = formData.rightColumn.find((f) => f.id === 'f10')?.value;
  const daysField = formData.rightColumn.find((f) => f.id === 'f11');

  if (invoiceDateStr && dueDateStr && daysField) {
    const d1 = new Date(invoiceDateStr);
    const d2 = new Date(dueDateStr);

    const diffTime = d2.getTime() - d1.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

    daysField.value = diffDays.toString();
  } else if (daysField) {
    daysField.value = '';
  }
};

const setFormattedFieldValue = (field: FormField, value: ApiValue) => {
  field.value = String(value ?? '');
};

const handleFieldInput = (event: Event, field: FormField) => {
  const target = event.target as HTMLInputElement | null;
  if (!target) {
    return;
  }

  field.value = target.value;
  errorFields.value.delete(field.id);

  if (field.id === 'f14') {
    vatIsAutoFilled.value = false;
  }
};

const parseFieldValue = (value: string | undefined): number => {
  const n = parseFloat((value ?? '').replace(/\s/g, '').replace(',', '.'));
  return isNaN(n) ? 0 : n;
};

const calcAutoVat = (
  total: number,
  vatType: string | undefined,
  pennyAdjust: number,
): number => {
  const isReversed = vatType === 'REVERSE' || vatType === 'EUINTERNAL';
  const rate = isReversed ? 0.25 : 0.2;
  return Math.round((total - pennyAdjust) * rate * 100) / 100;
};

// track if auto filled or user typed vat field
const vatIsAutoFilled = ref(false);

const isVatFieldDisabled = computed(
  () =>
    selectedVatType.value !== VatType.Normal && selectedVatType.value !== '',
);

const prefillVat = () => {
  if (selectedVatType.value !== VatType.Normal) {
    if (vatField.value) vatField.value.value = '';
    vatIsAutoFilled.value = false;
    return;
  }

  if (!vatIsAutoFilled.value && vatField.value?.value) return;

  const total = parseFieldValue(totalField.value?.value);

  if (!total) {
    if (vatField.value) vatField.value.value = '';
    vatIsAutoFilled.value = false;
    return;
  }

  if (vatField.value) {
    const pennyAdjust = parseFieldValue(
      formData.rightColumn.find((f) => f.id === 'f125')?.value,
    );
    vatField.value.value = String(
      calcAutoVat(total, selectedVatType.value, pennyAdjust),
    );
    vatIsAutoFilled.value = true;
  }
};

watch(() => totalField.value?.value, prefillVat);
watch(
  () => formData.rightColumn.find((f) => f.id === 'f125')?.value,
  prefillVat,
);
watch(selectedVatType, prefillVat);

const loadData = async () => {
  uploadedFile.value = null;
  const inboxDocumentId = route.query.inboxDocumentId;
  if (inboxDocumentId && typeof inboxDocumentId === 'string') {
    overlaySpinnerService.show();
    try {
      const fileData =
        await outlookInboxService.getSyncedInboxDocumentDetails(
          inboxDocumentId,
        );
      if (!fileData) {
        throw Error('No fileData');
      }
      uploadedFile.value = {
        fileId: fileData.id,
        blob: fileData.blob,
        name: fileData.filename,
      };
      isFromInboxDocument.value = true;
    } catch (error) {
      console.error(t('adminCreateInvoice.couldNotFetchData'), error);
      isFromInboxDocument.value = false;
    } finally {
      overlaySpinnerService.hide();
    }
  }

  try {
    const [fetchedAccounts, fetchedSuppliers] = await Promise.all([
      fetchAccounts(),
      adminSupplierInvoiceService.getAllSuppliers(),
    ]);
    accounts.value = fetchedAccounts;
    suppliers.value = fetchedSuppliers.map((supplier) =>
      financialConverter.toReadable(supplier),
    );
  } catch (error) {
    console.error(t('adminCreateInvoice.couldNotFetchData'), error);
  }
};

type RecordFormdata = Parameters<NewSupplierSaveEmit>[0] & {
  id: string;
  supplierNumber: string;
};

const recordToFormdataMap = (key: keyof RecordFormdata, value: string) => {
  switch (key) {
    case 'organisationNumber':
      return { leftColumn: [{ id: 'f1', value }] };
    case 'bankGiro':
      return { leftColumn: [{ id: 'f2', value, paymentType: 'BG' }] } as const;
    case 'postGiro':
      return { leftColumn: [{ id: 'f2', value, paymentType: 'PG' }] } as const;
    case 'streetName':
      return { leftColumn: [{ id: 'f3', value }] };
    case 'buildingNumber':
      return { leftColumn: [{ id: 'f35', value }] };
    case 'zipCode':
      return { leftColumn: [{ id: 'f4', value }] };
    case 'city':
      return { leftColumn: [{ id: 'f5', value }] };
    case 'vatNumber':
      return { leftColumn: [{ id: 'f8', value }] };
    case 'accountNumber':
      return { bottomRows: [{ id: 'bottom1', value }] };
    case 'vatType':
      return { bottomRows: [{ id: 'bottom3', value }] };
    default:
      return null;
  }
};

const recordFormatToFormdataFormat = (data: RecordFormdata) => {
  const selectedSupplier = formatSupplierName({
    name: data.name,
    supplierNumber: data.supplierNumber,
  });
  const updatedFormFields = Object.entries(data)
    .map(([key, value]) => {
      return recordToFormdataMap(key as keyof RecordFormdata, value);
    })
    .filter((field) => field !== null);

  const mergedFormFields = updatedFormFields.reduce(
    (acc, curr) => {
      if (curr.leftColumn) {
        acc.leftColumn.push(...curr.leftColumn);
      }
      if (curr.bottomRows) {
        acc.bottomRows.push(...curr.bottomRows);
      }
      return acc;
    },
    {
      leftColumn: [] as {
        id: string;
        value: string;
        paymentType?: 'BG' | 'PG';
      }[],
      bottomRows: [] as { id: string; value: string }[],
    },
  );

  return {
    selectedSupplier,
    formFields: mergedFormFields,
  };
};

const generateNewFormData = (
  formData: FormDataStructure,
  data: ReturnType<typeof recordFormatToFormdataFormat>,
) => {
  return {
    ...formData,
    selectedSupplier: data.selectedSupplier,
    leftColumn: formData.leftColumn.map((field) => {
      const updatedField = data.formFields.leftColumn.find(
        (f) => f.id === field.id,
      );
      return updatedField
        ? {
            ...field,
            value: updatedField.value,
            paymentType: updatedField.paymentType,
          }
        : field;
    }),
    bottomRows: formData.bottomRows.map((field) => {
      const updatedField = data.formFields.bottomRows.find(
        (f) => f.id === field.id,
      );
      return updatedField ? { ...field, value: updatedField.value } : field;
    }),
  };
};

const createInvoiceSupplier: NewSupplierSaveEmit = async (data) => {
  try {
    const result = await adminSupplierService.createSupplier(data);

    if (!result) {
      notificationService.open({
        text: t('adminCreateInvoice.errorDuringSave'),
        type: 'danger',
      });
      return;
    }

    const newInvoiceSupplier = recordFormatToFormdataFormat({
      ...data,
      ...result,
    });

    const newFormData = generateNewFormData(formData, newInvoiceSupplier);

    suppliers.value = (await adminSupplierInvoiceService.getAllSuppliers()).map(
      (supplier) => financialConverter.toReadable(supplier),
    );

    formData.selectedSupplier = data.name;
    formData.leftColumn = newFormData.leftColumn;
    formData.bottomRows = newFormData.bottomRows;
  } catch {
    notificationService.open({
      text: t('adminCreateInvoice.invoiceSavedUnsuccessfully'),
      type: 'danger',
    });
  } finally {
    closeNewSupplierModal();
  }
};

watch(
  () => [
    formData.rightColumn.find((f) => f.id === 'f9')?.value,
    formData.rightColumn.find((f) => f.id === 'f10')?.value,
  ],
  () => {
    calculateDaysDiff();
  },
);

onActivated(async () => {
  await loadData();
});

onMounted(loadData);

watch(
  () => formData.selectedSupplier,
  (newVal) => {
    if (newVal) {
      errorFields.value.delete('supplier');
      const supplierData = suppliers.value.find((s) => s.name === newVal);

      if (supplierData) {
        currentSupplierNumber.value = supplierData.supplierNumber || '';
        formData.leftColumn.forEach((field) => {
          switch (field.id) {
            case 'f1':
              setFormattedFieldValue(field, supplierData.organisationNumber);
              break;
            case 'f2':
              field.paymentType = supplierData.paymentType || 'BG';
              setFormattedFieldValue(
                field,
                field.paymentType === 'BG'
                  ? supplierData.bankGiro
                  : supplierData.postGiro,
              );
              break;
            case 'f3':
              field.value = supplierData.streetName || '';
              break;
            case 'f35':
              field.value = supplierData.buildingNumber || '';
              break;
            case 'f4':
              setFormattedFieldValue(field, supplierData.zipCode);
              break;
            case 'f5':
              field.value = supplierData.city || '';
              break;
            case 'f8':
              field.value = supplierData.vatNumber || '';
              break;
            default:
              break;
          }
        });
      }
    }
  },
);

const formatFormDataToApiBody = (data: FormDataStructure) => {
  const getVal = (id: string) =>
    [...data.leftColumn, ...data.rightColumn].find((f) => f.id === id)?.value ||
    '';

  const getBottomVal = (id: string) =>
    data.bottomRows.find((f) => f.id === id)?.value || '';

  const parseAmount = (fieldId: string) => {
    const stringVal = getVal(fieldId).replace(/\s/g, '').replace(',', '.');
    return parseFloat(stringVal) || 0;
  };

  const getAccountDetails = () => {
    const selectedId = getBottomVal('bottom1');
    const accountObj = accounts.value.find((acc) => acc.value == selectedId);

    if (accountObj) {
      return {
        number: Number(accountObj.value),
        name: accountObj.text ? accountObj.text.replace(/^\d+\s*-\s*/, '') : '',
      };
    }

    return {
      number: selectedId ? Number(selectedId) : null,
      name: null,
    };
  };

  const paymentField = data.leftColumn.find((f) => f.id === 'f2');
  const type = paymentField?.paymentType;
  const rawPaymentVal = paymentField?.value || '';
  const isBg = type === 'BG' || type === t('adminCreateInvoice.paymentType.bg');
  const isPg = type === 'PG' || type === t('adminCreateInvoice.paymentType.pg');

  const { number: accountNumber, name: accountName } = getAccountDetails();
  const inboxId = route.query.inboxDocumentId?.toString();
  return {
    supplier: {
      name: data.selectedSupplier,
      supplierNumber: currentSupplierNumber.value,
      organisationNumber: getVal('f1'),
      streetName: getVal('f3'),
      buildingNumber: getVal('f35'),
      zipCode: getVal('f4'),
      city: getVal('f5'),
      bankGiro: isBg && rawPaymentVal ? rawPaymentVal : null,
      postGiro: isPg && rawPaymentVal ? rawPaymentVal : null,
      vatNumber: getVal('f8'),
    },
    invoice: {
      fortnoxId: null,
      projectId: getBottomVal('bottom2'),
      invoiceDate: getVal('f9'),
      dueDate: getVal('f10'),
      termsOfPayment: getVal('f11'),
      ocr: getVal('f12'),
      invoiceNumber: getVal('f15'),
      pennyAdjust: parseAmount('f125'),
      totalAmount: parseAmount('f13'),
      vat: parseAmount('f14'),
      currency: getVal('f16'),

      accountNumber: accountNumber,
      accountName: accountName,

      vatType: getBottomVal('bottom3'),
      status: '',
      bankGiro: isBg && rawPaymentVal ? rawPaymentVal : null,
      postGiro: isPg && rawPaymentVal ? rawPaymentVal : null,
      notes: data.note,
    },
    document: {
      blob: inboxId ? null : (uploadedFile.value?.blob ?? null),
      name: uploadedFile.value?.name ?? null,
      inboxDocumentId: inboxId || null,
    },
  };
};

const resetInvoiceSpecificFields = () => {
  formData.rightColumn.forEach((field) => {
    field.value = '';
  });

  formData.bottomRows.forEach((field) => {
    field.value = '';
  });

  formData.note = '';
};

const validateRequirement = (
  condition: unknown,
  errorMessage: string,
): boolean => {
  if (!condition) {
    notificationService.open({
      text: errorMessage,
      type: 'danger',
    });
    return false;
  }
  return true;
};

const validateAllFields = (): boolean => {
  const missingFields: string[] = [];
  const errors = new Set<string>();

  // Validate supplier
  if (!formData.selectedSupplier) {
    missingFields.push(t('adminCreateInvoice.chooseSupplier'));
    errors.add('supplier');
  }

  // Validate left column fields
  formData.leftColumn.forEach((field) => {
    if (field.id === 'f2') return;
    if (!field.value || field.value.trim() === '') {
      missingFields.push(field.label);
      errors.add(field.id);
    }
  });

  // Validate right column fields
  formData.rightColumn.forEach((field) => {
    if (field.id === 'f12' || field.id === 'f125' || field.id === 'f14') return;
    if (!field.value || field.value.trim() === '') {
      missingFields.push(field.label);
      errors.add(field.id);
    }
  });

  // Validate bottom rows fields
  formData.bottomRows.forEach((field) => {
    if (!field.value || field.value.trim() === '') {
      missingFields.push(field.label);
      errors.add(field.id);
    }
  });

  if (missingFields.length > 0) {
    errorFields.value = errors;
    const fieldList = missingFields.join(', ');
    notificationService.open({
      text: `${t('adminCreateInvoice.requiredFields.missingFields')}: ${fieldList}`,
      type: 'danger',
    });
    return false;
  }

  errorFields.value.clear();
  return true;
};

const handleSave = async () => {
  try {
    if (!validateAllFields()) {
      return;
    }

    if (
      !validateRequirement(
        formData.selectedSupplier,
        t('adminCreateInvoice.requiredFields.supplierRequired'),
      )
    ) {
      return;
    }

    if (
      !validateRequirement(
        uploadedFile.value,
        t('adminCreateInvoice.requiredFields.invoicePdfRequired'),
      )
    ) {
      return;
    }

    const apiBody = formatFormDataToApiBody(formData);

    await adminSupplierInvoiceService.createSupplierInvoice(apiBody);

    notificationService.open({
      text: t('adminCreateInvoice.invoiceSavedSuccessfully'),
      type: 'success',
    });

    resetInvoiceSpecificFields();
    router.push({
      name: RouteName.AdminSupplierInvoices,
    });
  } catch (error) {
    console.error(t('adminCreateInvoice.errorDuringSave'), error);
    if (isClientError(error, 'DUPLICATE_INVOICE_ERROR')) return;
    if (error instanceof FinancialConverterError) {
      const key = `financialConverter.errors.${error.type}`;
      notificationService.open({
        type: 'danger',
        text: `${t(`${key}.message`)} (${t(`${key}.format`)})`,
      });
      return;
    }
    notificationService.open({
      text: t('adminCreateInvoice.invoiceSavedUnsuccessfully'),
      type: 'danger',
    });
  }
};

const openNewSupplierModal = () => {
  isNewSupplierModalOpen.value = true;
};

const closeNewSupplierModal = () => {
  isNewSupplierModalOpen.value = false;
};
</script>

<template>
  <div class="page-wrapper">
    <div class="content-columns">
      <div class="card-container">
        <h2 class="title is-4 page-title">
          {{ t('adminCreateInvoice.createNewInvoice') }}
        </h2>
        <form @submit.prevent="handleSave">
          <div class="header-section">
            <div class="supplier-controls-wrapper">
              <div class="existing-supplier-container">
                <div class="select-wrapper">
                  <DropDown
                    :items="supplierOptions"
                    v-model="formData.selectedSupplier"
                    :label="t('adminCreateInvoice.chooseSupplier')"
                    :class="{ 'field-error': errorFields.has('supplier') }"
                  />
                </div>
              </div>
              <button
                type="button"
                class="toggle-btn"
                @click="openNewSupplierModal"
              >
                <PhPlus size="16" />
                {{ t('adminCreateInvoice.newSupplier') }}
              </button>
            </div>
            <div class="next-number-hint">
              <span class="hint-label">{{
                t('adminCreateInvoice.serialNumber')
              }}</span>
              <span class="hint-value">-</span>
            </div>
          </div>

          <div class="grid-columns">
            <div class="column">
              <div
                v-for="field in formData.leftColumn"
                :key="field.id"
                class="input-group"
              >
                <label :for="field.id">{{ field.label }}</label>

                <div v-if="field.id === 'f2'" class="joined-input-group">
                  <div class="select-wrapper-compact">
                    <select v-model="field.paymentType" class="compact-select">
                      <option value="BG">
                        {{ t('adminCreateInvoice.paymentType.bg') }}
                      </option>
                      <option value="PG">
                        {{ t('adminCreateInvoice.paymentType.pg') }}
                      </option>
                    </select>
                    <svg
                      class="select-arrow"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 9l-7 7-7-7"
                      ></path>
                    </svg>
                  </div>
                  <input
                    :id="field.id"
                    :value="field.value"
                    type="text"
                    :placeholder="
                      field.paymentType === 'BG' ? '123-4567' : '98765-4'
                    "
                    :class="{ 'field-error': errorFields.has(field.id) }"
                    @input="handleFieldInput($event, field)"
                  />
                </div>

                <input
                  v-else
                  :id="field.id"
                  :value="field.value"
                  type="text"
                  placeholder="-"
                  :class="{ 'field-error': errorFields.has(field.id) }"
                  @input="handleFieldInput($event, field)"
                />
              </div>
            </div>

            <div class="column">
              <div
                v-for="field in formData.rightColumn"
                :key="field.id"
                class="input-group"
              >
                <label :for="field.id">{{ field.label }}</label>

                <template v-if="field.id === 'f16'">
                  <DropDown
                    :items="currencyOptions"
                    v-model="field.value"
                    :label="t('adminCreateInvoice.chooseCurrency')"
                  />
                </template>

                <input
                  v-else-if="field.id === 'f14'"
                  class="compact-select"
                  :class="{ 'field-error': errorFields.has(field.id) }"
                  :id="field.id"
                  type="text"
                  :value="field.value"
                  :disabled="isVatFieldDisabled"
                  placeholder="-"
                  @input="handleFieldInput($event, field)"
                />

                <input
                  v-else
                  class="compact-select"
                  :class="{
                    'negative-value':
                      field.id === 'f11' && Number(field.value) < 0,
                    'positive-value':
                      field.id === 'f11' &&
                      Number(field.value) >= 0 &&
                      field.value !== '',
                    'field-error': errorFields.has(field.id),
                  }"
                  :id="field.id"
                  :value="field.value"
                  :type="['f9', 'f10'].includes(field.id) ? 'date' : 'text'"
                  :disabled="field.id === 'f11'"
                  placeholder="-"
                  @input="handleFieldInput($event, field)"
                />
              </div>
            </div>
          </div>

          <div class="coding-section">
            <div
              v-for="field in formData.bottomRows"
              :key="field.id"
              class="input-group"
            >
              <label :for="field.id">{{ field.label }}</label>

              <template v-if="field.id === 'bottom1'">
                <DropDown
                  :items="accounts ?? []"
                  v-model="field.value"
                  :label="t('adminCreateInvoice.chooseAccount')"
                  :class="{ 'field-error': errorFields.has(field.id) }"
                  @update:modelValue="errorFields.delete(field.id)"
                />
              </template>

              <template v-else-if="field.id === 'bottom2'">
                <ProjectsDropDown
                  v-model="field.value"
                  :data="{
                    placeholder: t('adminCreateInvoice.chooseProject'),
                    includeArchived: false,
                  }"
                  :class="{ 'field-error': errorFields.has(field.id) }"
                  @action="errorFields.delete(field.id)"
                />
              </template>

              <template v-else-if="field.id === 'bottom3'">
                <DropDown
                  :items="vatTypeOptions"
                  v-model="field.value"
                  :label="t('adminCreateInvoice.chooseVatType')"
                  :class="{ 'field-error': errorFields.has(field.id) }"
                  @update:modelValue="errorFields.delete(field.id)"
                />
              </template>
            </div>
          </div>

          <div class="footer-section">
            <div class="note-area">
              <textarea
                id="notes"
                v-model="formData.note"
                :placeholder="t('adminCreateInvoice.notes')"
              ></textarea>
            </div>
            <div class="actions">
              <button type="submit" class="btn btn-primary">
                {{ t('adminCreateInvoice.save') }}
              </button>
            </div>
          </div>
        </form>
      </div>

      <div class="card-container invoice-preview">
        <RenderInvoice
          v-if="uploadedFile"
          :file="uploadedFile"
          :change-file="!isFromInboxDocument"
          @remove="uploadedFile = null"
        />
        <InvoiceUploader v-else @file-loaded="handleFileLoaded" />
      </div>
    </div>

    <div class="new-supplier-wrapper">
      <AdminCreateInvoiceNewSupplier
        :is-open="isNewSupplierModalOpen"
        @close="closeNewSupplierModal"
        @save="createInvoiceSupplier"
      />
    </div>
    <!-- @save="handleNewSupplierSave" -->
  </div>
</template>

<style scoped lang="scss">
.card-container {
  width: 100%;
  background: linear-gradient(180deg, #eef2f6 0%, #f1f5f9 100%);
  padding: 1rem 1.5rem;
  border-radius: 12px;
  height: fit-content;
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 10px 15px -3px rgba(0, 0, 0, 0.1);
  font-family:
    -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: #334155;
  border: 1px solid #cbd5e1;
  position: relative;
  margin-right: 0;

  &.invoice-preview {
    min-width: 400px;
    position: sticky;
    overflow: auto;
  }
}

.page-wrapper {
  gap: 20px !important;
  justify-content: flex-start !important;

  .new-supplier-wrapper {
    height: 20px;
  }
}

@media (max-width: 1500px) {
  .page-wrapper {
    flex-direction: column;
  }
  .card-container {
    max-width: 100%;
  }
}

.supplier-controls-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.existing-supplier-container {
  min-width: 400px;
  flex: 0 1 auto;
}

.select-wrapper {
  width: 100%;
}

.toggle-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.625rem 1.25rem;
  border-radius: 9999px;
  border: 1px solid #3b82f6;
  background-color: #3b82f6;
  color: white;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.2s;

  &:hover {
    background-color: #2563eb;
    border-color: #2563eb;
  }

  &:active {
    background-color: #1d4ed8;
    border-color: #1d4ed8;
  }
}

.next-number-hint {
  display: flex;
  justify-content: flex-start;
  font-size: 0.8rem;
  margin-top: 4px;
  color: #64748b;
  font-weight: 500;
  padding-right: 2px;
  margin-left: 10px;
}

.hint-value {
  color: #3b82f6;
  font-weight: 600;
}

.select-wrapper-compact {
  position: relative;
  width: 55px;
  flex-shrink: 0;
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.8rem;
}

.field-error {
  border-color: #ef4444 !important;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1) !important;
}

.field-error:focus {
  border-color: #dc2626 !important;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.2) !important;
}
</style>
